import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  usePromoCodes,
  useCreatePromoCode,
  useDeactivatePromoCode,
} from '../../hooks/usePromoCodes';

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

export default function PromoCodesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>(
    'percent',
  );
  const [discountValue, setDiscountValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [formError, setFormError] = useState('');

  const { data: promoCodes, isLoading, error } = usePromoCodes();
  const createPromoCode = useCreatePromoCode();
  const deactivatePromoCode = useDeactivatePromoCode();

  const resetForm = () => {
    setCode('');
    setDiscountType('percent');
    setDiscountValue('');
    setMaxUses('');
    setExpiresAt('');
    setFormError('');
  };

  const handleCreate = () => {
    setFormError('');

    if (!code.trim()) {
      setFormError('Code is required');
      return;
    }

    const value = parseInt(discountValue, 10);
    if (isNaN(value) || value < 1) {
      setFormError('Discount value must be at least 1');
      return;
    }

    if (discountType === 'percent' && value > 100) {
      setFormError('Percentage discount cannot exceed 100');
      return;
    }

    const parsedMaxUses = maxUses ? parseInt(maxUses, 10) : null;
    if (maxUses && (isNaN(parsedMaxUses!) || parsedMaxUses! < 1)) {
      setFormError('Max uses must be a positive number');
      return;
    }

    createPromoCode.mutate(
      {
        code: code.trim().toUpperCase(),
        discountType,
        discountValue: value,
        maxUses: parsedMaxUses,
        expiresAt: expiresAt || null,
      },
      {
        onSuccess: () => {
          resetForm();
          setCreateOpen(false);
        },
        onError: (err) => {
          setFormError(err.message || 'Failed to create promo code');
        },
      },
    );
  };

  const handleDeactivate = (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this promo code?')) {
      return;
    }
    deactivatePromoCode.mutate(id);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load promo codes. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Promo Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ minHeight: 44 }}
        >
          Create
        </Button>
      </Box>

      {/* Promo code cards */}
      <Stack spacing={1.5}>
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Skeleton variant="text" width="40%" height={28} />
                  <Skeleton variant="text" width="60%" height={20} sx={{ mt: 0.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={60} height={24} />
                  </Box>
                </CardContent>
              </Card>
            ))
          : promoCodes && promoCodes.length > 0
            ? promoCodes.map((promo) => {
                const isExpired =
                  promo.expiresAt && new Date(promo.expiresAt) < new Date();
                const isMaxed =
                  promo.maxUses !== null && promo.usedCount >= promo.maxUses;

                return (
                  <Card
                    key={promo.id}
                    sx={{
                      opacity: !promo.isActive || isExpired || isMaxed ? 0.6 : 1,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      {/* Code + discount */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <LocalOfferIcon
                              sx={{ fontSize: 18, color: 'primary.main' }}
                            />
                            <Typography
                              variant="body1"
                              fontWeight={700}
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: { xs: 16, sm: 18 },
                              }}
                            >
                              {promo.code}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: 13, sm: 14 } }}
                          >
                            {promo.discountType === 'percent'
                              ? `${promo.discountValue}% off`
                              : `${formatCurrency(promo.discountValue)} off`}
                          </Typography>
                        </Box>

                        {promo.isActive && !isExpired && !isMaxed && (
                          <IconButton
                            onClick={() => handleDeactivate(promo.id)}
                            size="small"
                            aria-label="Deactivate"
                            disabled={deactivatePromoCode.isPending}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Status chips */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          mt: 1.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Chip
                          label={promo.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={promo.isActive ? 'success' : 'default'}
                          sx={{ fontSize: 12, height: 26 }}
                        />
                        {isExpired && (
                          <Chip
                            label="Expired"
                            size="small"
                            color="warning"
                            sx={{ fontSize: 12, height: 26 }}
                          />
                        )}
                        {isMaxed && (
                          <Chip
                            label="Max uses reached"
                            size="small"
                            color="warning"
                            sx={{ fontSize: 12, height: 26 }}
                          />
                        )}
                      </Box>

                      {/* Usage stats */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1.5, fontSize: { xs: 13, sm: 14 } }}
                      >
                        Used: {promo.usedCount}
                        {promo.maxUses !== null ? ` / ${promo.maxUses}` : ''}
                        {' | '}
                        Expires: {formatDate(promo.expiresAt)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          fontSize: { xs: 12, sm: 13 },
                        }}
                      >
                        Created: {formatDate(promo.createdAt)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })
            : !isLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <LocalOfferIcon
                    sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    No promo codes yet. Create one to get started.
                  </Typography>
                </Box>
              )}
      </Stack>

      {/* Create Promo Code Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => {
          resetForm();
          setCreateOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 3 }, m: { xs: 0, sm: 2 } },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Create Promo Code
          </Typography>
          <IconButton
            onClick={() => {
              resetForm();
              setCreateOpen(false);
            }}
            edge="end"
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Code */}
            <TextField
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. SUMMER2024"
              fullWidth
              required
              slotProps={{
                input: { sx: { minHeight: 44, fontFamily: 'monospace' } },
              }}
            />

            {/* Discount Type */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Discount Type
              </Typography>
              <RadioGroup
                row
                value={discountType}
                onChange={(e) =>
                  setDiscountType(e.target.value as 'percent' | 'fixed')
                }
              >
                <FormControlLabel
                  value="percent"
                  control={<Radio />}
                  label="Percentage"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                />
                <FormControlLabel
                  value="fixed"
                  control={<Radio />}
                  label="Fixed amount"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                />
              </RadioGroup>
            </Box>

            {/* Discount Value */}
            <TextField
              label={
                discountType === 'percent'
                  ? 'Percentage (1-100)'
                  : 'Amount (cents)'
              }
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percent' ? '10' : '500'}
              fullWidth
              required
              helperText={
                discountType === 'fixed' && discountValue
                  ? `= ${formatCurrency(parseInt(discountValue, 10) || 0)}`
                  : undefined
              }
              slotProps={{
                input: { sx: { minHeight: 44 } },
              }}
            />

            {/* Max Uses */}
            <TextField
              label="Max Uses (optional)"
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              fullWidth
              slotProps={{
                input: { sx: { minHeight: 44 } },
              }}
            />

            {/* Expiry Date */}
            <TextField
              label="Expires At (optional)"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                input: { sx: { minHeight: 44 } },
              }}
            />
          </Stack>

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              resetForm();
              setCreateOpen(false);
            }}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createPromoCode.isPending}
            startIcon={
              createPromoCode.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            sx={{ minHeight: 44 }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
